'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { Camera, Search, Loader2, ArrowLeft, Mail, ShieldCheck } from 'lucide-react';
import { SelfieCapture } from './SelfieCapture';
import { PhotoResultsGrid } from './PhotoResultsGrid';
import { PhotoConsentGate } from './PhotoConsentGate';
import { PhotoEventHero } from './PhotoEventHero';
import { FacialRecognitionNotice } from './FacialRecognitionNotice';
import type { PhotoConsentInput } from '@/lib/race-photos/consent';
import { PhotoPurchaseForm } from './PhotoPurchaseModal';
import { formatPriceCents } from '@/lib/utils/currency';
import { useIsMobile } from '@/hooks/use-mobile';
import { photoRequestHeaders } from '@/lib/race-photos/client-id';
import { fetchThroughBusy } from '@/lib/race-photos/busy-retry';
import { mergePickedPhotos } from '@/lib/race-photos/browse-merge';
import { EmailTypoHint } from '@/components/ui/email-typo-hint';

type Step = 'selfie' | 'searching' | 'results' | 'payment' | 'free_email' | 'no_results' | 'verify_email' | 'verify_code';

interface ThemeColors {
  primary: string;
  secondary: string;
  text: string;
  textMuted: string;
  buttonText?: string;
}

interface RacePhotoFinderProps {
  albumId: string;
  albumToken: string;
  /**
   * Where this finder's endpoints live. An album link passes
   * `/api/race-photos/<albumId>`; the event's master link passes
   * `/api/race-photos/event/<token>`. Defaults to the album form, so the album
   * page is unchanged.
   */
  apiBase?: string;
  /**
   * Master-link scope has no per-album shortcuts yet: finding your photos by
   * the email you registered with, and redeeming a pre-purchase, are both
   * album-scoped features. Hidden rather than offered and then failing.
   */
  supportsEmailLookup?: boolean;
  eventName: string;
  participantPrice: number;
  participantPriceCurrency: string;
  coverImageUrl?: string;
  /** optional phone crop; falls back to coverImageUrl */
  mobileCoverImageUrl?: string;
  /** drives the removal route offered in the facial-recognition notice */
  selfRedactionEnabled?: boolean;
  /** where the notice sends someone who wants to be removed */
  removalHref?: string;
  themeColors: ThemeColors;
  initialDownloadToken?: string;
  initialEmail?: string;
}

function formatPrice(cents: number, currency: string) {
  return formatPriceCents(cents, currency);
}

export function RacePhotoFinder({
  albumId,
  albumToken,
  apiBase,
  supportsEmailLookup = true,
  eventName,
  participantPrice,
  participantPriceCurrency,
  coverImageUrl,
  mobileCoverImageUrl,
  selfRedactionEnabled = false,
  removalHref,
  themeColors,
  initialDownloadToken,
  initialEmail,
}: RacePhotoFinderProps) {
  // One base for every call, so this component works for an album link and the
  // event's master link without knowing which it is inside.
  const base = apiBase ?? `/api/race-photos/${albumId}`;
  const isMobile = useIsMobile();
  // Recorded consent for facial-recognition search. Null until the gate is
  // completed: the camera never opens and no search fires without it.
  const [consent, setConsent] = useState<PhotoConsentInput | null>(null);
  const [step, setStep] = useState<Step>(
    initialDownloadToken ? 'results' : (initialEmail && supportsEmailLookup) ? 'verify_email' : 'selfie'
  );
  const [matchedPhotoIds, setMatchedPhotoIds] = useState<string[]>([]);
  const [previews, setPreviews] = useState<{ id: string; previewUrl: string | null; filename: string }[]>([]);
  /**
   * Photos that matched, but not confidently enough for us to hand over on our
   * own judgement. Raising the confident threshold stopped strangers appearing
   * in downloads, and necessarily costs some genuine matches too; these are
   * that band. The athlete decides, because they know what they look like.
   */
  const [possibleMatches, setPossibleMatches] = useState<{ id: string; previewUrl: string | null; filename: string }[]>([]);
  /**
   * Browsing every published photo, when the organiser has allowed it. The
   * fallback for the athlete face matching cannot help: photos with no
   * detectable face, and genuine matches a safe threshold has to miss.
   */
  const [browseEnabled, setBrowseEnabled] = useState(false);
  const [browsePhotos, setBrowsePhotos] = useState<{ id: string; previewUrl: string | null; fullPreviewUrl?: string | null; filename: string }[]>([]);
  const [browseCursor, setBrowseCursor] = useState<string | null>(null);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [pickedBrowse, setPickedBrowse] = useState<Set<string>>(new Set());
  /** Open in place under the results, rather than navigating away from them. */
  const [browseOpen, setBrowseOpen] = useState(false);
  /** Index into browsePhotos for the lightbox, or null when it is closed. */
  const [browseLightbox, setBrowseLightbox] = useState<number | null>(null);

  const loadBrowse = async (cursor?: string | null): Promise<number> => {
    setBrowseLoading(true);
    try {
      const url = new URL(`${base}/browse`, window.location.origin);
      if (cursor) url.searchParams.set('cursor', cursor);
      const res = await fetch(url.toString(), { headers: photoRequestHeaders(false) });
      if (!res.ok) return 0;
      const data = await res.json();
      setBrowseEnabled(!!data.enabled);
      const page = ((data.photos || []) as { id: string; previewUrl: string | null; fullPreviewUrl?: string | null; filename: string | null }[])
        .map((x) => ({ ...x, filename: x.filename || 'Race photo' }));
      setBrowsePhotos((prev) => (cursor ? [...prev, ...page] : page));
      setBrowseCursor(data.nextCursor || null);
      return page.length;
    } finally {
      setBrowseLoading(false);
    }
  };

  const toggleBrowsePick = (id: string) => {
    setPickedBrowse((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  /**
   * Step through the lightbox, pulling the next page in at the end rather than
   * stopping dead. Browsing is a hunt, and the hunt should not end at photo 60.
   */
  const moveLightbox = async (delta: number) => {
    if (browseLightbox === null) return;
    const next = browseLightbox + delta;
    if (next < 0) return;
    if (next >= browsePhotos.length) {
      if (!browseCursor || browseLoading) return;
      const added = await loadBrowse(browseCursor);
      if (added > 0) setBrowseLightbox(next);
      return;
    }
    setBrowseLightbox(next);
  };

  /** Where a swipe began, so a drag across the photo can page it on a phone. */
  const browseTouchRef = useRef<{ x: number; y: number } | null>(null);
  /**
   * Zoomed into the photo currently open. A race photo shown to fit a 390px
   * phone puts the faces at a few dozen pixels, which cannot answer "is that
   * me" any better than the thumbnail did, so the viewer has to let people in
   * closer and pan around.
   */
  const [browseZoom, setBrowseZoom] = useState(false);
  const browseZoomRef = useRef<HTMLDivElement | null>(null);

  // Ask once on mount purely to know whether to offer it. The endpoint answers
  // enabled:false cheaply, so this costs nothing when browsing is off.
  useEffect(() => { loadBrowse(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A new photo always opens fitted. Carrying a zoom across photos leaves
  // people looking at a corner of an image they have not seen whole.
  useEffect(() => { setBrowseZoom(false); }, [browseLightbox]);

  useEffect(() => {
    if (browseLightbox === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setBrowseLightbox(null);
      else if (e.key === 'ArrowLeft') void moveLightbox(-1);
      else if (e.key === 'ArrowRight') void moveLightbox(1);
    };
    window.addEventListener('keydown', onKey);
    // Without this the page behind scrolls under the photo on a phone, which
    // reads as the overlay itself being broken.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [browseLightbox, browsePhotos.length, browseCursor, browseLoading]); // eslint-disable-line react-hooks/exhaustive-deps
  const [pickedPossible, setPickedPossible] = useState<Set<string>>(new Set());
  const [selfieS3Key, setSelfieS3Key] = useState('');
  const [downloadToken, setDownloadToken] = useState<string | null>(initialDownloadToken || null);
  const [issuingToken, setIssuingToken] = useState(false);
  const [freeEmail, setFreeEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** Shown while we are waiting out a busy server, so the spinner is not silent. */
  const [busyNotice, setBusyNotice] = useState<string | null>(null);

  // Pre-purchase verification state
  const [verifyEmail, setVerifyEmail] = useState(initialEmail || '');
  const [verifyCodeId, setVerifyCodeId] = useState<string | null>(null);
  const [verifyCodeInput, setVerifyCodeInput] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [isPrePurchased, setIsPrePurchased] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);

  // If arriving with a download token (from email link), fetch photo IDs
  useEffect(() => {
    if (!initialDownloadToken) return;

    const fetchPurchasedPhotos = async () => {
      try {
        const res = await fetch(`${base}/download?token=${initialDownloadToken}`, { headers: photoRequestHeaders(false) });
        if (res.ok) {
          const data = await res.json();
          if (data.photos?.length > 0) {
            setMatchedPhotoIds(data.photos.map((p: any) => p.id));
          }
        }
      } catch (err) {
        console.error('Failed to load purchased photos:', err);
      }
    };

    fetchPurchasedPhotos();
  }, [base, initialDownloadToken]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Every photo the athlete has claimed as theirs: what the search matched
   * confidently, plus each "might also be you" they ticked. Browse picks are
   * already folded into matchedPhotoIds when they tap "add to my photos".
   *
   * Derived ONCE and used by the grid, the free download and the paid purchase
   * alike, because those three had drifted apart. The grid counted the ticked
   * maybes; both purchase calls sent the bare search results. So an athlete
   * whose face matched nothing confidently, ticked three maybes and typed their
   * email sent an EMPTY list, which the server rejects with "matched photos
   * required", and saw "Could not prepare your downloads" however many times
   * they tried. Anyone with at least one confident match simply lost their
   * ticked maybes silently, which is why it looked like it worked for most
   * people.
   */
  const claimed = useMemo(
    // The same merge browsing uses, because it is the same question: these
    // photos ADD to what the search found, they never replace it.
    () => mergePickedPhotos(matchedPhotoIds, previews, possibleMatches, pickedPossible),
    [matchedPhotoIds, previews, possibleMatches, pickedPossible],
  );
  const claimedPhotoIds = claimed.matchedPhotoIds;
  const claimedPreviews = claimed.previews;

  // Free albums issue a download token without payment; the server emails the
  // participant a copy of the download link. Resolves to null on success, or to
  // the reason it failed so the athlete is told something they can act on.
  const issueFreeDownloadToken = async (email: string): Promise<string | null> => {
    try {
      const res = await fetch(`${base}/purchase`, {
        method: 'POST',
        headers: photoRequestHeaders(),
        body: JSON.stringify({ selfieS3Key, matchedPhotoIds: claimedPhotoIds, email }),
      });
      if (!res.ok) {
        // Keep the server's reason, as the paid form already does. A rate
        // limit, an unpublished album and a genuine failure need different
        // things from the athlete, and one flat sentence told them nothing.
        const detail = await res.json().catch(() => null);
        return (
          detail?.message
          || (res.status === 429
            ? 'Too many requests just now. Please wait a moment and try again.'
            : 'Could not prepare your downloads. Please try again.')
        );
      }
      const data = await res.json();
      if (!data.downloadToken) return 'Could not prepare your downloads. Please try again.';
      setDownloadToken(data.downloadToken);
      return null;
    } catch {
      return 'Could not reach us just now. Please check your connection and try again.';
    }
  };

  const handleFreeEmailSubmit = async () => {
    const email = freeEmail.trim();
    if (!email) return;
    if (claimedPhotoIds.length === 0) {
      setError('Pick at least one photo first.');
      return;
    }
    setIssuingToken(true);
    setError(null);
    const failure = await issueFreeDownloadToken(email);
    setIssuingToken(false);
    if (failure) {
      setError(failure);
    } else {
      setStep('results');
    }
  };

  const handleSelfieCapture = async (dataUrl: string) => {
    setStep('searching');
    setError(null);
    setBusyNotice(null);

    try {
      // Waits through a busy server rather than failing the athlete. A 503 here
      // means we could not look right now, not that their photos are missing,
      // and those must never look the same on screen.
      const res = await fetchThroughBusy(
        `${base}/selfie`,
        {
          method: 'POST',
          headers: photoRequestHeaders(),
          body: JSON.stringify({ selfieDataUrl: dataUrl, consent }),
        },
        {
          onWaiting: ({ waitMs }) =>
            setBusyNotice(`Lots of people are searching right now. Trying again in ${Math.round(waitMs / 1000)}s…`),
        },
      );
      setBusyNotice(null);

      if (!res.ok) {
        // Keep the server's reason: a rate limit, an unpublished album and a
        // genuine failure are very different messages to show a participant.
        const detail = await res.json().catch(() => null);
        throw new Error(
          detail?.message
            || (res.status === 429
              ? 'Too many searches. Please wait a moment and try again.'
              : 'Could not search for your photos. Please try again.'),
        );
      }

      const data = await res.json();
      setSelfieS3Key(data.selfieS3Key);
      setPreviews(data.previews || []);
      setPossibleMatches(((data.possibleMatches || []) as { id: string; previewUrl: string | null; filename: string | null }[])
        .map((p) => ({ ...p, filename: p.filename || 'Race photo' })));
      setPickedPossible(new Set());

      // "No confident matches" is not the same as "nothing to show". Someone
      // whose face only matches weakly is precisely who the possible-match
      // panel exists for, and sending them to no_results hides it from the one
      // person who needs it.
      if (data.matchCount === 0 && (data.possibleMatches || []).length === 0) {
        setStep('no_results');
      } else {
        setMatchedPhotoIds(data.matchedPhotoIds || []);

        setStep('results');
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
      setStep('selfie');
    }
  };

  const handleRequestDownload = () => {
    setError(null);
    // Free albums collect an email so we can send a copy of the download link;
    // paid albums collect it in the payment form.
    setStep(participantPrice === 0 ? 'free_email' : 'payment');
  };

  const handleRetakeSelfie = () => {
    setMatchedPhotoIds([]);
    setPreviews([]);
    setDownloadToken(null);
    setError(null);
    setStep('selfie');
  };

  const handlePurchaseSuccess = (token: string) => {
    setDownloadToken(token);
    setStep('results');
  };

  const handleVerifyEmail = async () => {
    if (!verifyEmail.trim()) return;
    setVerifyLoading(true);
    setError(null);

    try {
      const res = await fetch(`${base}/verify-email`, {
        method: 'POST',
        headers: photoRequestHeaders(),
        body: JSON.stringify({ email: verifyEmail.trim() }),
      });

      const data = await res.json();

      if (!data.prePurchased) {
        // Not a pre-purchaser - go to normal selfie flow
        setIsPrePurchased(false);
        setStep('selfie');
        return;
      }

      if (data.albumReady === false) {
        setError(data.message || 'Your photos are not ready yet.');
        return;
      }

      setIsPrePurchased(true);
      setVerifyCodeId(data.codeId);
      setMaskedEmail(data.maskedEmail);
      setStep('verify_code');
    } catch (err: any) {
      setError('Something went wrong. Please try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verifyCodeId || !verifyCodeInput.trim()) return;
    setVerifyLoading(true);
    setError(null);

    try {
      // Verify the code via the standard route
      const res = await fetch('/api/verification/verify-code', {
        method: 'POST',
        headers: photoRequestHeaders(),
        body: JSON.stringify({ codeId: verifyCodeId, code: verifyCodeInput.trim() }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.message || 'Invalid code. Please try again.');
        return;
      }

      // Code verified - now proceed to selfie capture
      // The selfie handler will use the redeem endpoint instead of purchase
      setStep('selfie');
    } catch (err: any) {
      setError('Something went wrong. Please try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleSelfieForRedemption = async (dataUrl: string) => {
    setStep('searching');
    setError(null);

    try {
      const res = await fetch(`${base}/selfie`, {
        method: 'POST',
        headers: photoRequestHeaders(),
        body: JSON.stringify({ selfieDataUrl: dataUrl, consent }),
      });

      if (!res.ok) {
        // Keep the server's reason: a rate limit, an unpublished album and a
        // genuine failure are very different messages to show a participant.
        const detail = await res.json().catch(() => null);
        throw new Error(
          detail?.message
            || (res.status === 429
              ? 'Too many searches. Please wait a moment and try again.'
              : 'Could not search for your photos. Please try again.'),
        );
      }

      const data = await res.json();
      setSelfieS3Key(data.selfieS3Key);
      setPreviews(data.previews || []);
      setPossibleMatches(((data.possibleMatches || []) as { id: string; previewUrl: string | null; filename: string | null }[])
        .map((p) => ({ ...p, filename: p.filename || 'Race photo' })));
      setPickedPossible(new Set());

      // As above: weak-only matches still have something to offer.
      if (data.matchCount === 0 && (data.possibleMatches || []).length === 0) {
        setStep('no_results');
        return;
      }

      setMatchedPhotoIds(data.matchedPhotoIds);

      // Redeem the pre-purchase
      const redeemRes = await fetch(`${base}/redeem`, {
        method: 'POST',
        headers: photoRequestHeaders(),
        body: JSON.stringify({
          codeId: verifyCodeId,
          code: verifyCodeInput.trim(),
          matchedPhotoIds: data.matchedPhotoIds,
          selfieS3Key: data.selfieS3Key,
        }),
      });

      if (redeemRes.ok) {
        const redeemData = await redeemRes.json();
        setDownloadToken(redeemData.downloadToken);
      }

      setStep('results');
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
      setStep('selfie');
    }
  };

  const priceLabel = participantPrice === 0
    ? 'Free download'
    : `${formatPrice(participantPrice, participantPriceCurrency)} per person`;

  return (
    <div>
      <PhotoEventHero
        eventName={eventName}
        eyebrow="Race Photos"
        subtitle="Take a selfie and find your photos instantly"
        badge={priceLabel}
        coverImageUrl={coverImageUrl}
        mobileCoverImageUrl={mobileCoverImageUrl}
        primary={themeColors.primary}
        secondary={themeColors.secondary}
      />

      {/* Content */}
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '32px 16px' }}>
        {/* Step: Verify Email (pre-purchase redemption) */}
        {step === 'verify_email' && (
          <div style={{ textAlign: 'center' }}>
            <Mail size={36} style={{ color: themeColors.secondary, margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--fv-text-primary)', margin: '0 0 8px' }}>
              Verify your email
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--fv-text-secondary)', margin: '0 0 24px' }}>
              Enter the email you used when registering to access your pre-purchased photos.
            </p>
            {error && (
              <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: '8px', color: '#EF4444', fontSize: '13px', marginBottom: '16px' }}>
                {error}
              </div>
            )}
            <form onSubmit={(e) => { e.preventDefault(); handleVerifyEmail(); }} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input
                type="email"
                value={verifyEmail}
                onChange={(e) => setVerifyEmail(e.target.value)}
                placeholder="your@email.com"
                required
                style={{
                  width: '100%', padding: '14px 16px', borderRadius: '10px',
                  border: '1px solid var(--fv-border)', background: 'var(--fv-bg-input)',
                  color: 'var(--fv-text-primary)', fontSize: '16px', boxSizing: 'border-box',
                }}
              />
              <EmailTypoHint email={verifyEmail} onAccept={setVerifyEmail} accentColor={themeColors.secondary} />
              <button
                type="submit"
                disabled={verifyLoading || !verifyEmail.trim()}
                style={{
                  padding: '14px 32px', borderRadius: '10px',
                  background: themeColors.secondary, color: themeColors.buttonText || '#fff',
                  border: 'none', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
                  opacity: verifyLoading ? 0.6 : 1, minHeight: '48px',
                }}
              >
                {verifyLoading ? 'Checking...' : 'Continue'}
              </button>
            </form>
            <button
              onClick={() => { setIsPrePurchased(false); setStep('selfie'); }}
              style={{
                marginTop: '20px', background: 'none', border: 'none',
                color: 'var(--fv-text-secondary)', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline',
              }}
            >
              I haven&apos;t pre-purchased, find and buy photos instead
            </button>
          </div>
        )}

        {/* Step: Verify Code */}
        {step === 'verify_code' && (
          <div style={{ textAlign: 'center' }}>
            <ShieldCheck size={36} style={{ color: themeColors.secondary, margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--fv-text-primary)', margin: '0 0 8px' }}>
              Enter verification code
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--fv-text-secondary)', margin: '0 0 24px' }}>
              We sent a 6-digit code to {maskedEmail || 'your email'}
            </p>
            {error && (
              <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: '8px', color: '#EF4444', fontSize: '13px', marginBottom: '16px' }}>
                {error}
              </div>
            )}
            <form onSubmit={(e) => { e.preventDefault(); handleVerifyCode(); }} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={verifyCodeInput}
                onChange={(e) => setVerifyCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                autoFocus
                style={{
                  width: '100%', padding: '14px 16px', borderRadius: '10px',
                  border: '1px solid var(--fv-border)', background: 'var(--fv-bg-input)',
                  color: 'var(--fv-text-primary)', fontSize: '24px', fontWeight: 700,
                  textAlign: 'center', letterSpacing: '8px', boxSizing: 'border-box',
                  fontFamily: 'var(--font-geist-mono, monospace)',
                }}
              />
              <button
                type="submit"
                disabled={verifyLoading || verifyCodeInput.length !== 6}
                style={{
                  padding: '14px 32px', borderRadius: '10px',
                  background: themeColors.secondary, color: themeColors.buttonText || '#fff',
                  border: 'none', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
                  opacity: (verifyLoading || verifyCodeInput.length !== 6) ? 0.6 : 1, minHeight: '48px',
                }}
              >
                {verifyLoading ? 'Verifying...' : 'Verify & find my photos'}
              </button>
            </form>
            <button
              onClick={() => { setStep('verify_email'); setVerifyCodeInput(''); setError(null); }}
              style={{
                marginTop: '16px', background: 'none', border: 'none',
                color: 'var(--fv-text-secondary)', fontSize: '13px', cursor: 'pointer',
              }}
            >
              <ArrowLeft size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              Use a different email
            </button>
          </div>
        )}

        {/* Step: Selfie */}
        {step === 'selfie' && (
          <div style={{ textAlign: 'center' }}>
            {consent && (
              <>
                <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--fv-text-primary)', margin: '0 0 8px' }}>
                  Take a Selfie
                </h2>
                <p style={{ fontSize: '14px', color: 'var(--fv-text-secondary)', margin: '0 0 24px' }}>
                  Our AI will find you in the event photos
                </p>
              </>
            )}
            {error && (
              <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: '8px', color: '#EF4444', fontSize: '13px', marginBottom: '16px' }}>
                {error}
              </div>
            )}
            {!consent ? (
              // Consent is taken, and recorded, before the camera opens.
              <PhotoConsentGate themeColors={themeColors} onConsent={setConsent} />
            ) : (
              <SelfieCapture onCapture={isPrePurchased ? handleSelfieForRedemption : handleSelfieCapture} />
            )}
            {!isPrePurchased && participantPrice > 0 && supportsEmailLookup && (
              <button
                onClick={() => { setStep('verify_email'); }}
                style={{
                  marginTop: '20px', background: 'none', border: 'none',
                  color: 'var(--fv-text-secondary)', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline',
                }}
              >
                I pre-purchased my photos
              </button>
            )}
          </div>
        )}

        {/* Step: Searching */}
        {step === 'searching' && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <Loader2 size={40} style={{ color: 'var(--fv-accent)', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--fv-text-primary)', margin: '0 0 8px' }}>
              Searching for your photos...
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--fv-text-secondary)', margin: 0 }}>
              {busyNotice || 'This usually takes a few seconds'}
            </p>
          </div>
        )}

        {/* Step: No Results */}
        {step === 'no_results' && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <Search size={40} style={{ color: 'var(--fv-text-secondary)', margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--fv-text-primary)', margin: '0 0 8px' }}>
              No Photos Found
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--fv-text-secondary)', margin: '0 0 24px' }}>
              We couldn&apos;t find any photos matching your face. Try again with better lighting.
            </p>
            <button
              onClick={() => setStep('selfie')}
              className="btn-primary"
              style={{
                padding: '12px 32px', borderRadius: '10px',
                background: 'var(--fv-accent)', color: 'var(--fv-accent-text, #fff)',
                border: 'none', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
              }}
            >
              Try Again
            </button>
          </div>
        )}


        {/* Step: Results */}
        {step === 'results' && (
          <PhotoResultsGrid
            albumId={albumId}
            apiBase={base}
            matchedPhotoIds={claimedPhotoIds}
            previews={claimedPreviews}
            possibleMatches={possibleMatches}
            pickedPossible={pickedPossible}
            onTogglePossible={(id: string) => setPickedPossible((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id); else next.add(id);
              return next;
            })}
            downloadToken={downloadToken}
            participantPrice={participantPrice}
            participantPriceCurrency={participantPriceCurrency}
            onRequestDownload={handleRequestDownload}
            onRetakeSelfie={handleRetakeSelfie}
            themeColors={themeColors}
          />
        )}

        {/* Browsing belongs here too, not only at the dead end.
            "I found two photos but I know there were more of me" is at least as
            common as finding nothing: a group shot the cap never indexed, or a
            genuine match the threshold had to reject. Offering it only after a
            total miss serves the rarer case and leaves the commoner one stuck. */}
        {(step === 'results' || step === 'no_results') && browseEnabled && (
          <div style={{
            marginTop: step === 'no_results' ? '0' : '22px',
            paddingTop: '18px',
            borderTop: '1px solid var(--fv-border)',
          }}>
            {!browseOpen ? (
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '13px', color: 'var(--fv-text-secondary)', margin: '0 0 10px' }}>
                  {step === 'no_results'
                    ? 'Sure you were photographed? Some photos have no clear face for us to match.'
                    : 'Think we missed some? Not every photo has a face we can match.'}
                </p>
                <button
                  onClick={() => setBrowseOpen(true)}
                  style={{
                    padding: '10px 22px', borderRadius: '10px', background: 'transparent',
                    color: 'var(--fv-accent)', border: '1px solid var(--fv-accent)',
                    fontSize: '14px', fontWeight: 600, cursor: 'pointer', minHeight: '44px',
                  }}
                >
                  Browse all photos
                </button>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
                  <h3 style={{ fontSize: '17px', fontWeight: 700, color: 'var(--fv-text-primary)', margin: 0 }}>
                    All photos
                  </h3>
                  <button
                    onClick={() => { setBrowseOpen(false); setPickedBrowse(new Set()); }}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '13px', color: 'var(--fv-text-secondary)', textDecoration: 'underline' }}
                  >
                    Close
                  </button>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--fv-text-secondary)', margin: '0 0 14px' }}>
                  {isMobile ? 'Tap a photo to see it bigger, then add it to yours.' : 'Click a photo to see it bigger, then add it to yours.'}
                </p>

                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px',
                  // Clear of the fixed action bar, so the last row is reachable.
                  paddingBottom: pickedBrowse.size > 0 ? '84px' : 0,
                }}>
                  {browsePhotos.map((photo, i) => {
                    const picked = pickedBrowse.has(photo.id);
                    const already = matchedPhotoIds.includes(photo.id);
                    return (
                      <div key={photo.id} style={{ position: 'relative', aspectRatio: '4/3' }}>
                        <button
                          type="button"
                          // Opens the lightbox rather than selecting. Deciding
                          // from a 120px tile is the thing this panel exists to
                          // avoid, so tapping the photo means "let me look",
                          // and the tick is the separate "that's me".
                          onClick={() => setBrowseLightbox(i)}
                          aria-label={`View ${photo.filename} larger`}
                          style={{
                            width: '100%', height: '100%', padding: 0, cursor: 'pointer', lineHeight: 0,
                            borderRadius: '8px', overflow: 'hidden', display: 'block',
                            background: 'var(--fv-bg-input)',
                            border: picked ? '2px solid var(--fv-accent)' : '1px solid var(--fv-border)',
                            opacity: already ? 0.5 : 1,
                          }}
                        >
                          {photo.previewUrl && (
                            <img src={photo.previewUrl} alt={photo.filename} loading="lazy"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          )}
                        </button>
                        {already ? (
                          <span aria-label="Already in your photos" title="Already in your photos" style={{
                            position: 'absolute', top: '6px', left: '6px', width: '24px', height: '24px',
                            borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'var(--fv-accent)', color: 'var(--fv-accent-text, #fff)', fontSize: '14px', fontWeight: 700,
                          }}>✓</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => toggleBrowsePick(photo.id)}
                            aria-pressed={picked}
                            aria-label={picked ? `Remove ${photo.filename}` : `Add ${photo.filename}`}
                            style={{
                              position: 'absolute', top: 0, left: 0, width: '36px', height: '36px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                            }}
                          >
                            <span style={{
                              width: '24px', height: '24px', borderRadius: '6px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '14px', fontWeight: 700, lineHeight: 1,
                              background: picked ? 'var(--fv-accent)' : 'rgba(0,0,0,0.45)',
                              color: picked ? 'var(--fv-accent-text, #fff)' : 'rgba(255,255,255,0.85)',
                              border: picked ? 'none' : '1px solid rgba(255,255,255,0.55)',
                            }}>{picked ? '✓' : ''}</span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {browseCursor && (
                  <div style={{ textAlign: 'center', marginTop: '14px' }}>
                    <button
                      onClick={() => loadBrowse(browseCursor)}
                      disabled={browseLoading}
                      style={{
                        padding: '10px 22px', borderRadius: '10px', background: 'transparent',
                        color: 'var(--fv-text-secondary)', border: '1px solid var(--fv-border)',
                        fontSize: '14px', cursor: browseLoading ? 'default' : 'pointer', minHeight: '44px',
                      }}
                    >
                      {browseLoading ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                )}

                {/* Fixed to the viewport, not sticky at the end of the grid.
                    Sticky put the bar wherever the scroll happened to be, so it
                    sat among the thumbnails and read as another tile. The one
                    control that finishes the job cannot be something you have to
                    find. */}
                {pickedBrowse.size > 0 && (
                  <div style={{
                    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50,
                    // Its own ground, so photos never show through the one
                    // element that has to stay readable. Taken from the page's
                    // background rather than hardcoded dark: a light-themed
                    // event gets a light bar. Not --fv-bg-card, which is
                    // `transparent` on transparent-box themes, which is how the
                    // bar came to have no ground at all.
                    background: 'var(--fv-page-bg, #0B0A09)',
                    borderTop: '1px solid var(--fv-border, rgba(255,255,255,0.12))',
                    boxShadow: '0 -8px 24px rgba(0,0,0,0.18)',
                    padding: `12px 16px calc(env(safe-area-inset-bottom) + 12px)`,
                  }}>
                    <button
                      onClick={() => {
                        // Merge, never replace: this panel adds to what the
                        // search already found.
                        const merged = mergePickedPhotos(matchedPhotoIds, previews, browsePhotos, pickedBrowse);
                        setMatchedPhotoIds(merged.matchedPhotoIds);
                        setPreviews(merged.previews);
                        setPickedBrowse(new Set());
                        setBrowseOpen(false);
                        setStep('results');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      style={{
                        display: 'block', width: '100%', maxWidth: '460px', margin: '0 auto',
                        minHeight: '48px', borderRadius: '10px', border: 'none',
                        background: themeColors.secondary, color: themeColors.buttonText || '#fff',
                        fontSize: '15px', fontWeight: 700, cursor: 'pointer', padding: '0 18px',
                      }}
                    >
                      Add {pickedBrowse.size} photo{pickedBrowse.size === 1 ? '' : 's'} to my photos
                    </button>
                  </div>
                )}

                {/* Full-size viewer. A 120px tile cannot answer the only
                    question browsing asks, so looking properly and choosing
                    have to happen in the same place: the tick lives in here,
                    not back in the grid. */}
                {browseLightbox !== null && browsePhotos[browseLightbox] && (() => {
                  const photo = browsePhotos[browseLightbox];
                  const picked = pickedBrowse.has(photo.id);
                  const already = matchedPhotoIds.includes(photo.id);
                  const hasNext = browseLightbox < browsePhotos.length - 1 || !!browseCursor;
                  return (
                    <div
                      role="dialog"
                      aria-modal="true"
                      aria-label="Photo viewer"
                      onClick={() => setBrowseLightbox(null)}
                      onTouchStart={(e) => { browseTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
                      onTouchEnd={(e) => {
                        const start = browseTouchRef.current;
                        browseTouchRef.current = null;
                        if (!start) return;
                        const dx = e.changedTouches[0].clientX - start.x;
                        const dy = e.changedTouches[0].clientY - start.y;
                        // Horizontal only, and clearly horizontal, so a scroll
                        // attempt does not skip the photo being looked at.
                        // While zoomed a drag is a pan, not a page turn.
                        if (browseZoom) return;
                        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) void moveLightbox(dx < 0 ? 1 : -1);
                      }}
                      style={{
                        // Opaque, not a scrim: at 6% pass-through the grid of
                        // thumbnails behind still reads clearly and competes with the
                        // photo being judged, which is the one thing this view is for.
                        position: 'fixed', inset: 0, zIndex: 60, background: '#0B0A09',
                        display: 'flex', flexDirection: 'column',
                        paddingBottom: 'env(safe-area-inset-bottom)', paddingTop: 'env(safe-area-inset-top)',
                      }}
                    >
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 12px', flexShrink: 0,
                      }}>
                        <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                          {browseLightbox + 1} of {browsePhotos.length}{browseCursor ? '+' : ''}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); setBrowseLightbox(null); }}
                          aria-label="Close viewer"
                          style={{
                            width: '44px', height: '44px', borderRadius: '10px', background: 'none',
                            border: 'none', color: '#fff', fontSize: '22px', cursor: 'pointer', lineHeight: 1,
                          }}
                        >
                          ✕
                        </button>
                      </div>

                      <div
                        ref={browseZoomRef}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          flex: 1, minHeight: 0, padding: '0 8px',
                          display: browseZoom ? 'block' : 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          overflow: browseZoom ? 'auto' : 'hidden',
                          WebkitOverflowScrolling: 'touch',
                        }}
                      >
                        <img
                          src={photo.fullPreviewUrl || photo.previewUrl || ''}
                          alt={photo.filename}
                          onClick={() => {
                            const next = !browseZoom;
                            setBrowseZoom(next);
                            // Land in the middle of the photo rather than its
                            // top-left corner, which is rarely where anyone is.
                            if (next) requestAnimationFrame(() => {
                              const box = browseZoomRef.current;
                              if (!box) return;
                              box.scrollLeft = (box.scrollWidth - box.clientWidth) / 2;
                              box.scrollTop = (box.scrollHeight - box.clientHeight) / 2;
                            });
                          }}
                          style={browseZoom
                            ? { width: '260%', maxWidth: 'none', height: 'auto', display: 'block', cursor: 'zoom-out' }
                            : { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '6px', cursor: 'zoom-in' }}
                        />
                      </div>

                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          gap: '10px', padding: '12px',
                        }}
                      >
                        <button
                          onClick={() => void moveLightbox(-1)}
                          disabled={browseLightbox === 0}
                          aria-label="Previous photo"
                          style={{
                            width: '48px', height: '48px', borderRadius: '10px', flexShrink: 0,
                            background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                            color: '#fff', fontSize: '18px', cursor: browseLightbox === 0 ? 'default' : 'pointer',
                            opacity: browseLightbox === 0 ? 0.35 : 1,
                          }}
                        >
                          ‹
                        </button>

                        {already ? (
                          <span style={{
                            flex: 1, maxWidth: '320px', height: '48px', borderRadius: '10px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)',
                            fontSize: '15px', fontWeight: 600,
                          }}>
                            Already in your photos
                          </span>
                        ) : (
                          <button
                            onClick={() => toggleBrowsePick(photo.id)}
                            aria-pressed={picked}
                            style={{
                              flex: 1, maxWidth: '320px', minHeight: '48px', borderRadius: '10px',
                              background: picked ? 'var(--fv-accent)' : 'rgba(255,255,255,0.12)',
                              color: picked ? 'var(--fv-accent-text, #fff)' : '#fff',
                              border: picked ? 'none' : '1px solid rgba(255,255,255,0.35)',
                              fontSize: '15px', fontWeight: 700, cursor: 'pointer', padding: '0 16px',
                            }}
                          >
                            {picked ? '✓ Added to my photos' : 'This is me'}
                          </button>
                        )}

                        <button
                          onClick={() => void moveLightbox(1)}
                          disabled={!hasNext || browseLoading}
                          aria-label="Next photo"
                          style={{
                            width: '48px', height: '48px', borderRadius: '10px', flexShrink: 0,
                            background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                            color: '#fff', fontSize: '18px', cursor: !hasNext || browseLoading ? 'default' : 'pointer',
                            opacity: !hasNext || browseLoading ? 0.35 : 1,
                          }}
                        >
                          ›
                        </button>
                      </div>

                      <p style={{
                        flexShrink: 0, textAlign: 'center', margin: '0 0 10px',
                        fontSize: '12px', color: 'rgba(255,255,255,0.55)', padding: '0 16px',
                      }}>
                        {browseZoom
                          ? `${isMobile ? 'Tap' : 'Click'} the photo to zoom back out.`
                          : pickedBrowse.size > 0
                            ? `${pickedBrowse.size} selected. Close to add them to your photos.`
                            : `${isMobile ? 'Tap' : 'Click'} the photo to zoom in. The watermark comes off the ones you download.`}
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Step: Free album email capture */}
        {step === 'free_email' && (
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => { setError(null); setStep('results'); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'none', border: 'none', color: 'var(--fv-text-secondary)',
                fontSize: '13px', cursor: 'pointer', marginBottom: '24px', padding: 0,
              }}
            >
              <ArrowLeft size={14} /> Back to photos
            </button>
            <Mail size={36} style={{ color: themeColors.secondary, margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--fv-text-primary)', margin: '0 0 8px' }}>
              Where should we send your photos?
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--fv-text-secondary)', margin: '0 0 24px' }}>
              Your downloads unlock straight away and we&apos;ll email you a copy of the link so you can come back any time.
            </p>
            {error && (
              <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: '8px', color: '#EF4444', fontSize: '13px', marginBottom: '16px' }}>
                {error}
              </div>
            )}
            <form onSubmit={(e) => { e.preventDefault(); handleFreeEmailSubmit(); }} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input
                type="email"
                value={freeEmail}
                onChange={(e) => setFreeEmail(e.target.value)}
                placeholder="your@email.com"
                required
                style={{
                  width: '100%', padding: '14px 16px', borderRadius: '10px',
                  border: '1px solid var(--fv-border)', background: 'var(--fv-bg-input)',
                  color: 'var(--fv-text-primary)', fontSize: '16px', boxSizing: 'border-box',
                }}
              />
              <EmailTypoHint email={freeEmail} onAccept={setFreeEmail} accentColor={themeColors.secondary} />
              <button
                type="submit"
                disabled={issuingToken || !freeEmail.trim()}
                style={{
                  padding: '14px 32px', borderRadius: '10px',
                  background: themeColors.secondary, color: themeColors.buttonText || '#fff',
                  border: 'none', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
                  opacity: issuingToken ? 0.6 : 1, minHeight: '48px',
                }}
              >
                {issuingToken ? 'Preparing your photos...' : 'Get my photos'}
              </button>
            </form>
          </div>
        )}

        {/* Step: Payment (inline) */}
        {step === 'payment' && (
          <div>
            <button
              onClick={() => setStep('results')}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'none', border: 'none', color: 'var(--fv-text-secondary)',
                fontSize: '13px', cursor: 'pointer', marginBottom: '24px', padding: 0,
              }}
            >
              <ArrowLeft size={14} /> Back to photos
            </button>
            <PhotoPurchaseForm
              albumId={albumId}
              apiBase={base}
              matchedPhotoIds={claimedPhotoIds}
              selfieS3Key={selfieS3Key}
              participantPrice={participantPrice}
              currency={participantPriceCurrency}
              themeColors={themeColors}
              onSuccess={handlePurchaseSuccess}
            />
          </div>
        )}
      </div>

      <style jsx global>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Transparency for everyone in the photos, not only the person
          searching: every face in every uploaded photo is indexed, so people in
          the background have biometric data processed too. */}
      <FacialRecognitionNotice
        removalHref={removalHref || `/photos/${albumToken}/remove`}
        selfRedactionEnabled={selfRedactionEnabled}
      />
    </div>
  );
}
